
import { Ball, SimulationConfig, MorphData, CollisionEvent, BlastEvent, ShapeType, TrailCircle, EscapeRing, BallPiece } from '@/types';
import { RhythmicMultiSimLogic } from './RhythmicMultiSimLogic';
import { BallBreakAnimation } from './animations/BallBreakAnimation';
import { SpectrumLogic } from './SpectrumLogic';
import { AUDIO } from '@/services/AudioService';
import { SHAPES } from '@/constants/constants';
import { SpawnerPhysics } from './modes/SpawnerPhysics';

// New Modular Imports
import { TrailLogic } from './core/TrailLogic';
import { DrawerLogic } from './core/DrawerLogic';
import { BasicLogic } from './core/BasicLogic';
import { RhythmicLogic } from './core/RhythmicLogic';
import { EscapeLogic } from './core/EscapeLogic';
import { SpaceSpiralLogic } from './core/SpaceSpiralLogic';
import { SpawnerLogic } from './core/SpawnerLogic';
import { PhysicsEngine } from './core/PhysicsEngine';

export interface SimulationState {
  now: number;
  deltaTime: number;
  currentTS: number;
  hue: number;
  rotRad: number;
  morphData: MorphData;
  simTime: number;
}

/**
 * Consolidates all simulation logic into a modular aggregator.
 */
export class CoreSimulationLogic {
  public static TrailLogic = TrailLogic;
  public static BasicLogic = BasicLogic;
  public static RhythmicLogic = RhythmicLogic;
  public static EscapeLogic = EscapeLogic;
  public static DrawerLogic = DrawerLogic;
  public static SpaceSpiralLogic = SpaceSpiralLogic;
  public static SpawnerLogic = SpawnerLogic;
  public static PhysicsEngine = PhysicsEngine;

  /**
   * Orchestrates the high-level update of the simulation state.
   */
  public static update(
    deltaTime: number,
    cfg: SimulationConfig,
    state: {
        balls: Ball[],
        trailCircles: TrailCircle[],
        collisionEvents: CollisionEvent[],
        blastEvents: BlastEvent[],
        escapeRings: EscapeRing[],
        hue: number,
        timeScale: number,
        isRotationStopped: boolean,
        shake: number,
        counterPop: number,
        timeRemaining: number,
        morphTimer: number,
        morphState: { active: boolean, from: ShapeType, to: ShapeType, progress: number },
        escapeAccelerationBoost: number,
        containerRadius: number,
        rhythmicTimeScale: number,
        rhythmicTimeScaleRate: number,
        rhythmicBreakPieces: BallPiece[],
        persistentSpectrumBoost: Float32Array,
        persistentSpectrumTarget: Float32Array,
        rhythmicResetTimeout: NodeJS.Timeout | null,
        rhythmicIndex: number,
        hyperSpeedActive: boolean,
        zoomLevel: number,
        simTime: number,
    },
    refs: any,
    externals: {
        setElapsedTime: (v: (p: number) => number) => void,
        setBounceCount: (v: (p: number) => number) => void,
        setBallCount: (v: number) => void,
        setConfig: (v: (p: SimulationConfig) => SimulationConfig) => void,
        setIsPlaying: (v: boolean) => void,
        triggerBlast: any,
        playCollisionSound: any,
        initSimulation: any,
        draw: any,
    }
  ) {
    const now = Date.now();
    let effectiveCfg = { ...cfg };

    // Enforcement: Non-spawner modes are limited to 1 ball
    if (effectiveCfg.mode !== 'SPAWNER') {
        effectiveCfg.initialBalls = 1;
        effectiveCfg.maxBalls = 1;
    }

    // 1. Feature Flag Mapping
    if (effectiveCfg.mode === 'RHYTHMIC' || effectiveCfg.mode === 'RHYTHMIC_ESCAPE') effectiveCfg.enableRhythmic = true;
    if (effectiveCfg.mode === 'ESCAPE' || effectiveCfg.mode === 'RHYTHMIC_ESCAPE') effectiveCfg.enableEscape = true;
    if (effectiveCfg.mode === 'TRAIL') effectiveCfg.enableTrails = true;
    if (effectiveCfg.mode === 'DRAWER') effectiveCfg.enableDrawer = true;
    if (effectiveCfg.mode === 'SPAWNER') effectiveCfg.enableSpawner = true;
    if (effectiveCfg.mode === 'SPACE_SPIRAL') effectiveCfg.enableSpaceSpiral = true;

    // 2. Spectrum Update
    if (effectiveCfg.rhythmicSpectrumEnabled) {
        SpectrumLogic.update(deltaTime, effectiveCfg, AUDIO.getFrequencyData(), state.persistentSpectrumBoost, state.persistentSpectrumTarget, state.balls);
    }

    // 3. State & Visual Updates
    if (effectiveCfg.rgbLighting) state.hue = (state.hue + effectiveCfg.hueCycleSpeed) % 360;
    if (state.shake > 0) { state.shake *= effectiveCfg.shakeDecayRate; if (state.shake < 0.1) state.shake = 0; }
    if (state.counterPop > 1.0) { state.counterPop -= effectiveCfg.popDecayRate * (deltaTime / 16.67); if (state.counterPop < 1.0) state.counterPop = 1.0; }
    if (isNaN(state.counterPop) || !isFinite(state.counterPop)) state.counterPop = 1.0;

    // 4. Morph Logic
    CoreSimulationLogic.BasicLogic.handleMorph(deltaTime, effectiveCfg, {current: state.morphTimer} as any, {current: state.morphState} as any, SHAPES as any, externals.setConfig);

    const rotRad = (effectiveCfg.containerRotation || 0) * Math.PI / 180;
    const morphBaseRadius = (effectiveCfg.mode === 'RHYTHMIC_ESCAPE' ? effectiveCfg.rhythmicEscapeShieldRingRadius : effectiveCfg.containerRadius);
    const morphData: MorphData = state.morphState.active 
        ? { from: state.morphState.from, to: state.morphState.to, factor: state.morphState.progress, baseRadius: morphBaseRadius } 
        : { from: effectiveCfg.containerShape, to: effectiveCfg.containerShape, factor: 0, baseRadius: morphBaseRadius };

    // 5. Speed & Acceleration
    if (effectiveCfg.speedIncreaseMode === 'TIME') {
        state.timeScale += (refs.speedIncreaseRate.current) * (deltaTime / 1000);
        if (effectiveCfg.speedAcceleration !== 0) refs.speedIncreaseRate.current += effectiveCfg.speedAcceleration * (deltaTime / 1000);
    }

    if (effectiveCfg.enableRhythmic && effectiveCfg.rhythmicTimeScaleIncreaseMode === 'TIME') {
        refs.rhythmicTimeScale.current += (refs.rhythmicTimeScaleRate.current) * (deltaTime / 1000);
        if (effectiveCfg.rhythmicTimeScaleAcceleration !== 0) {
            refs.rhythmicTimeScaleRate.current += effectiveCfg.rhythmicTimeScaleAcceleration * (deltaTime / 1000);
        }
    }
    const currentTS = (state.timeScale * effectiveCfg.masterSpeed) + state.escapeAccelerationBoost;
    
    // Safety: Prevent NaN or infinite time scale propagation
    const safeTS = isNaN(currentTS) || !isFinite(currentTS) ? 1.0 : currentTS;
    
    state.simTime += deltaTime * safeTS;
    externals.setElapsedTime(prev => prev + deltaTime * safeTS);

    if (state.rhythmicBreakPieces.length > 0) {
        state.rhythmicBreakPieces = BallBreakAnimation.updatePieces(state.rhythmicBreakPieces, deltaTime, effectiveCfg.rhythmicBreakGravity, now);
    }

    // 6. Mode Specific Logic Implementation
    if (effectiveCfg.enableSpawner || effectiveCfg.mode === 'SPAWNER') {
        CoreSimulationLogic.SpawnerLogic.update(deltaTime, effectiveCfg, state.balls, now, (overrides) => {
            return refs.ballPool.current.get(
                overrides.id || `spawn-${now}-${Math.random()}`,
                overrides.pos || { x: 0, y: 0 },
                overrides.vel || SpawnerPhysics.getSpawnVelocity(effectiveCfg),
                overrides.radius || effectiveCfg.ballRadius,
                overrides.color || effectiveCfg.ballColor,
                overrides.mass || effectiveCfg.ballMass,
                effectiveCfg.ballStyle,
                overrides
            );
        }, externals.setBallCount);
    }

    if (effectiveCfg.enableSpaceSpiral && effectiveCfg.spaceSpiralEnableRounds) {
        if (CoreSimulationLogic.SpaceSpiralLogic.handleRoundLogic(now, refs.roundStartTime.current, effectiveCfg, state.balls, 0, 0, refs.currentRound, externals.setIsPlaying, () => { refs.roundStartTime.current = Date.now(); externals.initSimulation(); })) return { spawns: [], updatedState: state };
    }

    if (effectiveCfg.enableDrawer) {
        CoreSimulationLogic.DrawerLogic.manageGlobal(state.balls, effectiveCfg, now);
    }

    if (effectiveCfg.enableRhythmic && effectiveCfg.rhythmicMultiSimEnabled) {
        RhythmicMultiSimLogic.updateAll(refs.rhythmicSimulationsState.current, effectiveCfg, deltaTime, state.simTime, rotRad, state.hue, morphData, {current: state.collisionEvents} as any, {current: state.persistentSpectrumBoost} as any, {current: state.persistentSpectrumTarget} as any, {current: state.shake} as any, externals.triggerBlast, {current: state.rhythmicBreakPieces} as any, {current: state.rhythmicResetTimeout} as any, refs.ballPool.current, state.trailCircles, refs.trailCirclePool.current, externals.setBounceCount);
        state.balls = refs.rhythmicSimulationsState.current.map((s: any) => s.ball).filter((b: any) => b !== null) as Ball[];
    }

    if (effectiveCfg.enableEscape) {
        const rotationActive = !state.isRotationStopped && effectiveCfg.escapeRotationMode !== 'NONE';
        const shieldAngleDelta = effectiveCfg.escapeRotationSpeed * safeTS * 0.01;
        if (rotationActive) refs.rhythmicEscapeShieldAngle.current += shieldAngleDelta;

        // Sub-logic: Shield Time Disintegration
        if (refs.rhythmicEscapeShieldActive.current && effectiveCfg.rhythmicEscapeShieldRingDestroyMode === 'TIME') {
            refs.rhythmicEscapeShieldTime.current += (deltaTime / 1000) * safeTS;
            if (refs.rhythmicEscapeShieldTime.current >= (effectiveCfg.rhythmicEscapeShieldRingDestroyValue || 10)) {
                refs.rhythmicEscapeShieldActive.current = false;
                externals.triggerBlast({ pos: { x: 0, y: 0 }, color: '#ffffff', radius: effectiveCfg.rhythmicEscapeShieldRingRadius || 100 }, effectiveCfg.rhythmicEscapeShieldRingRadius || 100, 0, morphData, effectiveCfg.rhythmicEscapeShieldBreakAnimation || 'SUPERNOVA');
            }
        }
        // Use a standard for loop to avoid index skip issues when shifting
        for (let idx = 0; idx < state.escapeRings.length; idx++) {
            const ring = state.escapeRings[idx];
            let minRadiusOverride = refs.rhythmicEscapeShieldActive.current ? (effectiveCfg.rhythmicEscapeShieldRingRadius || 0) : 0;
            
            CoreSimulationLogic.EscapeLogic.updateRings(ring, effectiveCfg, safeTS, idx, state.isRotationStopped, state.hyperSpeedActive, state.escapeRings.length, minRadiusOverride);
            
            if (idx === 0 && ring.radius <= (minRadiusOverride + 0.1) && !refs.rhythmicEscapeShieldActive.current) {
                externals.triggerBlast({ pos: { x: 0, y: 0 }, color: `hsl(${(state.hue + ring.hue) % 360}, 100%, 50%)`, radius: 10 }, 10, ring.angle);
                if (effectiveCfg.escapeTimeChallengeMode) state.timeRemaining += Math.max(0, effectiveCfg.escapeTimeAddition);
                state.escapeRings.shift(); 
                externals.setBounceCount(prev => prev + 1); 
                state.counterPop = 1.35;
                idx--; // Adjust index because we shifted the array
            }
        }
    }

    // 7. Physics Substeps (Internalized)
    const spawns = CoreSimulationLogic.PhysicsEngine.handleSubsteps(
        deltaTime, effectiveCfg, state.balls, 0, 0, rotRad, state.hue, now, safeTS, morphData, state.collisionEvents, state.persistentSpectrumBoost, state.persistentSpectrumTarget, {current: state.containerRadius} as any, refs.ballGrowthRate, refs.containerShrinkRate, {current: state.timeScale} as any, refs.speedIncreaseRate, {current: state.shake} as any, {current: state.counterPop} as any, externals.setBounceCount, externals.setIsPlaying, externals.initSimulation, externals.triggerBlast, externals.playCollisionSound, {current: state.escapeRings} as any, state.isRotationStopped, refs.hyperSpeedState, {current: state.timeRemaining} as any, {current: state.escapeAccelerationBoost} as any, refs.physicsSystem.current, refs.ballPool.current, state.trailCircles, refs.trailCirclePool.current, refs.worker, refs.rhythmicIndex, refs.rhythmicTimeScale, refs.rhythmicTimeScaleRate, refs.rhythmicBreakPieces, refs.rhythmicResetTimeout, state.simTime, refs
    );

    // 8. Visual Logic Persistence
    if (isNaN(state.containerRadius)) state.containerRadius = effectiveCfg.containerRadius || 300;
    
    CoreSimulationLogic.BasicLogic.handleGrowthAndShrink(state.balls, effectiveCfg, deltaTime, {current: state.containerRadius} as any, refs.ballGrowthRate, refs.containerShrinkRate, morphData, 0, 0, (b) => { 
        if (!isNaN(state.containerRadius)) {
            externals.triggerBlast({ pos: { x: 0, y: 0 }, color: b.color, radius: b.radius }, state.containerRadius, undefined, morphData, 'PHYSICS_SHATTER'); 
        }
        externals.setIsPlaying(false); 
        setTimeout(() => { externals.initSimulation(true); externals.setIsPlaying(true); }, 2000); 
    });
    CoreSimulationLogic.BasicLogic.handleZoom(effectiveCfg, state.escapeRings.length, {current: state.zoomLevel} as any);
    CoreSimulationLogic.BasicLogic.enforceGlobalBoundaries(state.balls, effectiveCfg, rotRad, morphData, state.containerRadius);

    // Filter old collision events and recycle
    const activeCollisions = [];
    for (let i = 0; i < state.collisionEvents.length; i++) {
        const e = state.collisionEvents[i];
        if (now - e.startTime < (e.duration || 800) + 100) {
            activeCollisions.push(e);
        } else {
            if (refs.collisionEventPool && refs.collisionEventPool.current) {
                refs.collisionEventPool.current.release(e);
            }
        }
    }
    state.collisionEvents = activeCollisions;

    // Filter old blast events and recycle
    const activeBlasts = [];
    for (let i = 0; i < state.blastEvents.length; i++) {
        const e = state.blastEvents[i];
        if (now - e.startTime < (e.duration || 1000) + 200) {
            activeBlasts.push(e);
        } else {
            if (refs.blastEventPool && refs.blastEventPool.current) {
                refs.blastEventPool.current.release(e);
            }
        }
    }
    state.blastEvents = activeBlasts;

    if (spawns.length > 0) {
        spawns.forEach((s, idx) => {
          if (state.balls.length < effectiveCfg.maxBalls) {
              const b = refs.ballPool.current.get(`spawn-${now}-${idx}`, s.pos, SpawnerPhysics.getSpawnVelocity(effectiveCfg), effectiveCfg.ballRadius, s.color, effectiveCfg.ballMass, effectiveCfg.ballStyle);
              state.balls.push(b);
          }
        });
        externals.setBallCount(state.balls.length);
    }

    if (state.balls.some(b => b.shouldDisableBorders)) {
        if (effectiveCfg.escapeShowOuterBorders) {
            externals.setConfig(prev => ({ ...prev, escapeShowOuterBorders: false }));
        }
        state.balls.forEach(b => { b.shouldDisableBorders = false; });
    }

    return { spawns, updatedState: state };
  }
}
